import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { REFERENCE_CHART_PAIRS, candleTime, openReferenceChart, painted } from "./referenceChartWidget";
import { captureReferencePair } from "./referenceSetupCapture";
import ReferenceImageViewer from "./ReferenceImageViewer";

const dateInput = (time) => new Date(time * 1000).toISOString().slice(0, 16);
const normalizeSymbol = (value) => {
  let symbol = value.trim().toUpperCase().replace(/^BINANCE:/, "").replace(/\//g, "");
  if (!symbol.endsWith("USDT")) symbol += "USDT";
  if (!/^[A-Z0-9]{1,30}USDT$/.test(symbol)) throw new Error("Enter a Binance USDT Futures symbol, such as BTC or BTCUSDT.");
  return symbol;
};

export default function ReferenceChartPopup({ slot, timeframe, initialSymbol, initialDate, initialDecisionTime, onSave, onClose }) {
  const specs = REFERENCE_CHART_PAIRS[timeframe];
  const popupRef = useRef(null);
  const baseRef = useRef(null);
  const higherRef = useRef(null);
  const setupRef = useRef(null);
  const engine = useRef(null);
  const [symbolInput, setSymbolInput] = useState(initialSymbol || "BTCUSDT");
  const [loaded, setLoaded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [syncing, setSyncing] = useState(false);
  const [aligning, setAligning] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [utcInput, setUtcInput] = useState(initialDecisionTime
    ? dateInput(Date.parse(initialDecisionTime) / 1000 - specs[0].seconds)
    : initialDate ? `${initialDate}T00:00` : "");
  const [preview, setPreview] = useState(null);
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [previewView, setPreviewView] = useState("setup");
  const [error, setError] = useState("");
  const loadCount = useRef(0);
  const initialJump = useRef(null);

  const close = () => {
    if (busyRef.current) return;
    if (preview && !window.confirm("Discard this screenshot preview?")) return;
    onClose();
  };
  useLayoutEffect(() => {
    const previous = document.activeElement;
    popupRef.current?.querySelector("input")?.focus();
    return () => {
      if (engine.current) { engine.current.controller.abort(); clearTimeout(engine.current.timer); }
      previous?.focus?.();
    };
  }, []);

  const schedule = (time, moveBase) => {
    const state = engine.current;
    if (!state?.ready || busyRef.current || !Number.isFinite(time)) return;
    time = candleTime(time, specs[0]);
    if (time < 0 || time > Date.now() / 1000) return;
    state.desired = { time, moveBase };
    state.revision += 1;
    setPreview(null); setSyncing(true); setError("");
    clearTimeout(state.timer);
    state.timer = setTimeout(() => synchronize(state), 180);
  };
  const synchronize = async (state) => {
    if (state.running || state.controller.signal.aborted) return;
    state.running = true;
    setAligning(true);
    try {
      while (state.desired && !state.controller.signal.aborted) {
        const { time, moveBase } = state.desired;
        const revision = state.revision;
        state.desired = null;
        if (moveBase) {
          state.mutingBase = true;
          try { await state.charts[0].center(time); await painted(); }
          finally { state.mutingBase = false; }
        }
        await state.charts[1].center(time);
        await Promise.all(state.charts.map((chart) => chart.mark(time)));
        await painted();
        if (state.controller.signal.aborted) return;
        if (revision !== state.revision) continue;
        if (!state.charts.every((chart) => chart.hasCandle(time) && chart.showsCandle(time))) throw new Error("The candle is not visible on both charts. Choose a date within this symbol’s available history and use Go to candle.");
        state.anchor = time; state.syncedRevision = revision;
        setAnchor(time); setUtcInput(dateInput(time));
      }
    } catch (failure) {
      if (!state.controller.signal.aborted) { state.anchor = null; setAnchor(null); setError(failure.message); }
    } finally {
      state.running = false;
      if (!state.controller.signal.aborted) {
        if (state.desired) synchronize(state);
        else { setSyncing(false); setAligning(false); }
      }
    }
  };

  useEffect(() => {
    if (!loaded) return undefined;
    const state = { controller: new AbortController(), charts: [], ready: false, revision: 0, syncedRevision: -1, anchor: null };
    engine.current = state;
    setLoading(true); setAligning(false); setAnchor(null); setPreview(null); setError("");
    const rangeChanged = (range) => {
      if (busyRef.current && !state.controller.signal.aborted) { state.captureMoved = true; state.revision += 1; return; }
      if (!state.controller.signal.aborted && state.ready && !state.mutingBase && range?.to > range?.from) schedule((range.from + range.to) / 2, false);
    };
    const invalidateHigher = (range) => {
      if (busyRef.current && !state.controller.signal.aborted) { state.captureMoved = true; state.revision += 1; return; }
      if (!state.controller.signal.aborted && state.ready && !state.running && !state.desired && !busyRef.current && state.anchor != null) {
        setPreview(null);
        if (range?.to > range?.from && Math.abs((range.from + range.to) / 2 - state.anchor) > specs[1].seconds) schedule(state.anchor, false);
      }
    };
    Promise.all([
      openReferenceChart(baseRef.current, loaded.symbol, specs[0], state.controller.signal, rangeChanged, (time) => schedule(time, true)),
      openReferenceChart(higherRef.current, loaded.symbol, specs[1], state.controller.signal, invalidateHigher),
    ]).then((charts) => {
      if (state.controller.signal.aborted) return;
      state.charts = charts; state.ready = true;
      setLoading(false);
      const time = initialJump.current || candleTime(Date.now() / 1000, specs[0]) - specs[0].seconds;
      schedule(time, true);
    }).catch((failure) => {
      if (!state.controller.signal.aborted) {
        state.controller.abort(); setLoading(false); setSyncing(false); setError(failure.message);
      }
    });
    return () => { state.controller.abort(); clearTimeout(state.timer); };
    // Each load owns its widgets, subscriptions, and pending requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  const load = (event) => {
    event.preventDefault();
    if (busyRef.current) return;
    try {
      const symbol = normalizeSymbol(symbolInput);
      if (preview && !window.confirm("Discard this screenshot and load another chart?")) return;
      initialJump.current = utcInput ? parseUtc() : null;
      if (engine.current) { engine.current.ready = false; engine.current.controller.abort(); clearTimeout(engine.current.timer); }
      setLoading(true); setPreview(null); setAnchor(null);
      setLoaded({ symbol, id: ++loadCount.current });
    } catch (failure) { setError(failure.message); }
  };
  const parseUtc = () => {
    const time = Date.parse(`${utcInput}:00Z`) / 1000;
    if (!Number.isFinite(time) || time < 0 || time > Date.now() / 1000) throw new Error("Choose a valid UTC date and time in the past.");
    return candleTime(time, specs[0]);
  };
  const jump = () => {
    try { schedule(parseUtc(), true); } catch (failure) { setError(failure.message); }
  };
  const capture = async () => {
    const state = engine.current;
    if (!state?.ready || state.controller.signal.aborted || state.running || state.desired
        || state.anchor == null || state.syncedRevision !== state.revision || busyRef.current) return;
    busyRef.current = true; setBusy(true); setError("");
    state.captureMoved = false;
    const revision = state.revision;
    try {
      const images = await captureReferencePair({ charts: state.charts, symbol: loaded.symbol, time: state.anchor,
        slot, signal: state.controller.signal, container: setupRef.current });
      if (state.controller.signal.aborted) return;
      if (revision !== state.revision) throw new Error("The chart changed during capture. Capture again.");
      setPreview({ ...images, symbol: loaded.symbol, time: state.anchor });
      setPreviewView("setup");
      setPreviewExpanded(true);
    } catch (failure) { if (!state.controller.signal.aborted) setError(failure.message); }
    finally {
      busyRef.current = false;
      if (!state.controller.signal.aborted) {
        setBusy(false);
        if (state.captureMoved) schedule(state.anchor, true);
      }
    }
  };
  const save = async () => {
    if (!preview || busyRef.current) return;
    busyRef.current = true; setBusy(true); setError("");
    try {
      await onSave({ dataBase64: preview.dataUrl.split(",")[1], outcomeDataBase64: preview.outcomeDataUrl.split(",")[1],
        sourceSymbol: preview.symbol, eventDate: dateInput(preview.time).slice(0, 10),
        decisionTime: preview.decisionTime, capturePolicy: preview.capturePolicy });
      onClose();
    } catch (failure) { if (!engine.current?.controller.signal.aborted) setError(failure.message || "Screenshot could not be saved. Try again."); }
    finally { busyRef.current = false; if (!engine.current?.controller.signal.aborted) setBusy(false); }
  };
  const onKeyDown = (event) => {
    if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close(); }
    if (event.key === "Tab") {
      const items = [...popupRef.current.querySelectorAll('button:not(:disabled),input:not(:disabled),iframe,[tabindex="0"]')];
      const first = items[0]; const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      event.stopPropagation();
    }
  };
  const available = !!engine.current?.ready && !loading && !syncing && !busy && anchor != null;
  return <div className="adminReferenceChartBackdrop" onKeyDown={onKeyDown}>
    <section ref={popupRef} className="adminReferenceChartPopup" role="dialog" aria-modal="true" aria-labelledby="reference-chart-title">
      <header className="adminReferenceChartHeader"><div>
        <h3 id="reference-chart-title">{slot} · Capture reference charts</h3>
        <p>{specs[0].label} base + {specs[1].label} {specs[1].seconds > specs[0].seconds ? "higher timeframe" : "context"} · Binance USDT Futures · UTC</p>
      </div><button type="button" className="adminSecondaryButton" disabled={busy} onClick={close}>Close</button></header>
      <form className="adminReferenceChartControls" onSubmit={load}>
        <label className="adminFormField"><span>Symbol</span><input value={symbolInput} maxLength={32} disabled={busy}
          placeholder="BTCUSDT" onChange={(event) => setSymbolInput(event.target.value)} /></label>
        <button className="adminSecondaryButton" type="submit" disabled={busy || loading}>Load charts</button>
        <label className="adminFormField"><span>Base candle time (UTC)</span><input type="datetime-local" step="60" value={utcInput} disabled={busy}
          onChange={(event) => setUtcInput(event.target.value)} /></label>
        <button type="button" className="adminSecondaryButton" disabled={!engine.current?.ready || loading || busy} onClick={jump}>Go to candle</button>
      </form>
      <p>Drag the base chart to center a candle, or right-click it and choose “Select reference candle”. The second chart follows the same time. Charts are historical snapshots; reload to refresh current data.</p>
      <p>Capture saves two views: a setup at the selected base candle’s close, {specs[1].seconds > specs[0].seconds
        ? "including the developing higher candle reconstructed from completed base candles up to that time"
        : "using completed daily candles on both charts"}, and an outcome image with the later chart context for your review.</p>
      {error && <p className="adminAlert adminAlertError" role="alert">{error}</p>}
      {(loading || syncing) && <p role="status">{loading ? "Loading charts…" : "Aligning both charts…"}</p>}
      {busy && <p role="status">Preparing or saving both images…</p>}
      <div className="adminReferenceChartCanvases" inert={busy || aligning || loading ? "" : undefined}
        style={{ pointerEvents: busy || aligning || loading ? "none" : undefined }}>
        <div><h4>Base · {specs[0].label}</h4><div ref={baseRef} className="adminReferenceChartCanvas" /></div>
        <div><h4>{specs[1].seconds > specs[0].seconds ? "Higher" : "Context"} · {specs[1].label}</h4><div ref={higherRef} className="adminReferenceChartCanvas" /></div>
      </div>
      {anchor != null && <p>Selected candle opens: <strong>{dateInput(anchor).replace("T", " ")} UTC</strong>.
        Setup cutoff at its close: <strong>{dateInput(anchor + specs[0].seconds).replace("T", " ")} UTC</strong>.</p>}
      <div className="adminFormActions">
        <button type="button" className="adminSecondaryButton" disabled={!available} onClick={capture}>Capture setup and outcome</button>
        <button type="button" className="adminPrimaryButton" disabled={!preview || busy || syncing} onClick={save}>{busy ? "Working…" : `Save both images to ${slot}`}</button>
      </div>
      {preview && <div className="adminReferenceCaptureReview">
        <div className="adminReferenceCapturePair">{["setup", "outcome"].map((view) => <div key={view}>
          <h4>{view === "setup" ? "Setup · comparison image" : "Outcome · review only"}</h4>
          <button type="button" className="adminReferencePreviewButton" aria-haspopup="dialog"
            aria-expanded={previewExpanded && previewView === view} disabled={busy}
            onClick={() => { setPreviewView(view); setPreviewExpanded(true); }}>
            <img src={view === "setup" ? preview.dataUrl : preview.outcomeDataUrl} alt={`${slot} ${preview.symbol} ${view} charts`} />
            <span>Expand {view} preview</span>
          </button>
        </div>)}</div>
        <p>Saving adds both images to the slot’s draft. Use “Save new version” in the pack to keep them.</p></div>}
      {preview && previewExpanded && <ReferenceImageViewer src={previewView === "setup" ? preview.dataUrl : preview.outcomeDataUrl}
        title={`${slot} · ${preview.symbol} · ${previewView === "setup" ? "Setup" : "Outcome (review only)"}`}
        alt={`${slot} ${preview.symbol} ${previewView} charts`} onClose={() => setPreviewExpanded(false)} />}
      <div ref={setupRef} className="adminReferenceSetupRendering" aria-hidden="true" inert="" />
    </section>
  </div>;
}
